import {
  addAsset,
  addAssets,
  AssetInfo,
  AssetType,
  pointCloudType,
  CameraData,
  OrthoData,
  LineworkLayerData,
  XMLData,
  TagItemData,
  WebMapData,
  createAsset,
  createTags,
  TagAnnotation
} from "../redux/assets-slice";
import {Parser} from 'xml2js';
import {dialog, fse, isStaticSite} from "../electron-modules";
import path from 'path';
import slash from 'slash';
import {hideBackdrop, showBackdrop, toast} from "../app";
import {PythonExecutable} from "../executable";
import LocalScene, { isValidProjection } from "../viewer/js/projections";
import { loadProjectFile } from ".";
import {
  IFCFileFormat,
  imageOrthoFormat,
  imagePlanarFormat,
  pointCloudFileFormats,
  configFileType,
  SHPFileFormat,
  DXFFileFormat,
  xmlFileFormat,
  projFileFormat
} from "../file-extensions";
import { t } from "../localization";
import { readFileBuffer, readFileText } from "../viewer/js/file-system-loader";
import { Dispatch, nanoid } from "@reduxjs/toolkit";
import {curveDist, lineDist} from "../viewer/js/measurements/land-xml-calculation";
import { defaultTagTexture } from "../viewer/textures/tag-icons";
import { TagSize } from "../types/tags";
import { useDispatch } from "react-redux";
import { useGlobalSettings } from "../hooks";
import { openExternalLink } from "../urls";
import { updateDataProjection, updateViewProjection} from "../redux/projections-slice";
import * as CSV from 'csv-string';

const DEFAULT_LAYER_COLOR = "#FF0000";

/** Remove paths from existing array */
const removeFromArray = (objects, toRemove) => {
  return objects.filter(object => !toRemove.includes(object));
};

export const useAssetTools = () => {
  const dispatch = useDispatch();
  const {orthoQuality} = useGlobalSettings();

  const addAssetPaths = async (assetPaths: string[], folderID: string) => {
    let totalAssetsAdded = 0;

    const tagPaths = assetPaths.filter(isTagFile);
    const tagPathsAdded = await addTagCSVs(tagPaths, folderID, false);
    assetPaths = removeFromArray(assetPaths, tagPathsAdded);
    totalAssetsAdded += tagPathsAdded.length;

    const planarPaths = assetPaths.filter(isPlanarFile);
    totalAssetsAdded += await addPlanarFiles(planarPaths, folderID);

    const panoramicPaths = assetPaths.filter(isPanoramicFile);
    totalAssetsAdded += await addPanoramicFiles(panoramicPaths, folderID, false);

    const pointCloudPaths = assetPaths.filter(isPointCloudFile);
    totalAssetsAdded += await addPointCloudFiles(pointCloudPaths, folderID);

    const orthoPaths = assetPaths.filter(isOrthomosaicFile);
    totalAssetsAdded += await addOrthoFiles(orthoPaths, folderID);

    const modelPaths = assetPaths.filter(isModelFile);
    totalAssetsAdded += await addModelFiles(modelPaths, folderID);

    const xmlPaths = assetPaths.filter(isXmlFile);
    totalAssetsAdded += await addXMLFiles(xmlPaths, folderID);

    if (totalAssetsAdded === 0) return;

    toast.success(t("toast.added-project-files", {
      count: totalAssetsAdded
    }));
  };

  const addFilePaths = (filePaths: string[], folderID: string) => {
    if (!filePaths || filePaths.length === 0) return;

    const configs = filePaths.filter(isConfigFile);
    const assetPaths = filePaths.filter(isAssetFile);
    const projections = filePaths.filter(isProjectionFile);

    if (configs.length > 0) {
      // Load first config file (ignore other files)
      const success = loadProjectFile(configs[0]);
      if (success) {
        toast.success(t("toast.load-config-file"));
      }
      return;
    }

    if (projections.length > 0) {
      // Read projection from solv3d projection file
      const projectionPath = projections[0];
      const text = fse.readFileSync(projectionPath);
      const projection = JSON.parse(text);

      if (isValidProjection(projection.string)) {
        dispatch(updateDataProjection(projection));
        dispatch(updateViewProjection(projection));
      } else {
        toast.error("Invalid projection file");
      }
    }

    if (assetPaths.length > 0) {
      // Add new assets
      addAssetPaths(assetPaths, folderID);
    }
  };

  const addWebSource = (name: string, data: WebMapData, folderID: string) => {
    dispatch(addAsset({
      folderID,
      name,
      path: null,
      type: AssetType.Webmap,
      data
    }));
  };

  const addModelFiles = async (assetPaths: string[], folderID: string) => {
    const ifcPaths = assetPaths.filter(isIFCFile);
    const shpPaths = assetPaths.filter(isSHPFile);
    const dxfPaths = assetPaths.filter(isDXFFile);

    let modelsAdded = 0;
    modelsAdded += await addIFCFiles(dispatch, ifcPaths, folderID);
    modelsAdded += await addSHPFiles(dispatch, shpPaths, folderID);
    modelsAdded += await addDXFFiles(dispatch, dxfPaths, folderID);

    return modelsAdded;
  };

  const addPointCloudFiles = async (assetPaths: string[], folderID: string) => {
    let assetsList = [] as AssetInfo[];

    for (let pointPath of assetPaths) {
      const extension = path.extname(pointPath).slice(1);
      const type = pointCloudType(extension);
      const fileName = path.basename(pointPath);

      if (type === AssetType.Encompass) {
        pointPath = path.dirname(pointPath);
        if (fileName !== "r.idx") {
          toast.error(t('assets.error_loading_points_r_idx'));
          return;
        }
      }

      if (type === AssetType.Potree) {
        pointPath = path.dirname(pointPath);
        if (fileName !== "octree.bin") {
          toast.error(t('assets.error_loading_points_octree'));
          return;
        }
      }

      assetsList.push({
        folderID,
        name: fileName,
        path: pointPath,
        type
      });
    };

    dispatch(addAssets(assetsList));

    return assetPaths.length;
  };

  const addOrthoFiles = async (assetPaths: string[], folderID: string) => {
    showBackdrop();

    let numAdded = await addOrthoBatch(dispatch,
      assetPaths, folderID, orthoQuality);

    hideBackdrop();

    return numAdded;
  };

  const addTagCSVs = async (assetPaths: string[], folderID: string, catchErrors = true): Promise<string[]> => {
    showBackdrop();

    let pathsAdded = [] as string[];

    for (const assetPath of assetPaths) {
      try {
        const numberOfTags = await addTagCSV(dispatch, assetPath, folderID);
        pathsAdded.push(assetPath);

        toast.success(t("toast.added-new-tags", {
          count: numberOfTags
        }));
      } catch {
        if (!catchErrors) continue;
        const filename = path.basename(assetPath);

        toast.error(t("toast.tag-failed-parse", {
          filename
        }));
      }
    }

    hideBackdrop();

    return pathsAdded;
  };

  const addPanoramicFiles = async (assetPaths: string[], folderID: string, catchErrors = true) => {
    showBackdrop();

    let numFilesAdded = 0;

    for (const assetPath of assetPaths) {
      const fileName = path.basename(assetPath);

      try {
        const {numAdded, numMissing} = await addPanoramicFile(
          dispatch, assetPath, folderID);

        if (numMissing > 0) {
          toast.warning(t('assets.added_panoramic_images_missing', {
            numAdded,
            numMissing
          }));
        } else {
          toast.success(t('assets.added_panoramic_images', {
            numAdded
          }));
        }

        numFilesAdded += 1;
      } catch (event) {
        if (!catchErrors) continue;

        if (event.pathError) {
          toast.error(t("toast.import-csv-path-error", {
            path: fileName
          }));
        } else {
          toast.error(t("toast.import-csv-parse-error", {
            path: fileName
          }));
        }
      }
    }

    hideBackdrop();

    return numFilesAdded;
  };

  const addPlanarFiles = async (assetPaths: string[], folderID: string) => {
    showBackdrop();

    let numFilesAdded = 0;
    for (const assetPath of assetPaths) {
      const fileName = path.basename(assetPath);

      try {
        const {numAdded, numMissing} = await addPlanarFile(
          dispatch, assetPath, folderID);

        if (numMissing > 0) {
          toast.warning(t('assets.added_planar_images_missing', {
            numAdded,
            numMissing
          }));
        } else {
          toast.success(t('assets.added_planar_images', {
            numAdded
          }));
        }

        numFilesAdded += 1;
      } catch {
        toast.error(t("toast.import-planar-error", {
          path: fileName
        }));
      }
    }

    hideBackdrop();

    return numFilesAdded;
  };

  const addXMLFiles = async (assetPaths: string[], folderID: string) => {
    showBackdrop();

    let numAdded = 0;

    for (const assetPath of assetPaths) {
      const added = await addXMLFile(dispatch, assetPath, folderID);
      if (added) {
        numAdded++;
      }
    }

    hideBackdrop();

    return numAdded;
  };

  return {
    addAssetPaths,
    addFilePaths,
    addModelFiles,
    addPointCloudFiles,
    addOrthoFiles,
    addTagCSVs,
    addPanoramicFiles,
    addPlanarFiles,
    addXMLFiles,
    addWebSource
  };
};

/** Save a file based on name. Works for website and compiled exe */
export const downloadFile = async (name: string, data, fileType: string, fileFilter) => {
  if (isStaticSite) {
    const contentType = `data:text/${fileType};charset=utf-8`;
    const content = `${contentType},${encodeURIComponent(data)}`;
    openExternalLink(content, {name});
  } else {
    try {
      const savePath = await getPathFromDialog(name, fileFilter);
      if (!savePath) return false;

      await fse.writeFile(savePath, data);
    } catch {
      return false;
    }
  }

  return true;
};

const getPathFromDialog = async (name, filter) => {
  const result = await dialog.showSaveDialog({
    defaultPath: name,
    filters: filter,
  });

  const {filePath, canceled} = result;
  if (canceled || !filePath) return;

  const savePath = slash(filePath);
  return savePath;
};

export const pathWithExtension = (filename, extension) => {
  const basename = filename.replace(/\.[^.$]+$/, '');
  return `${basename}.${extension}`;
};

export const isAssetFile = (filename) => {
  return isPanoramicFile(filename)
    || isTagFile(filename)
    || isPlanarFile(filename)
    || isPointCloudFile(filename)
    || isOrthomosaicFile(filename)
    || isModelFile(filename)
    || isXmlFile(filename);
};

export const isModelFile = (filename) => {
  return isIFCFile(filename)
    || isSHPFile(filename)
    || isDXFFile(filename);
};

export const isPanoramicFile = (filename) => {
  return isCSVFile(filename)
    || isTXTFile(filename)
    || isRZMLFile(filename)
    || isODMFile(filename);
};

const isTagFile = filename => {
  return isCSVFile(filename);
};

export const isCSVFile = filename => {
  return path.extname(filename) === '.csv';
};

export const isTXTFile = filename => {
  return path.extname(filename) === '.txt';
};

export const isRZMLFile = filename => {
  return path.extname(filename) === '.rzml';
};

export const isODMFile = filename => {
  return path.basename(filename) === "shots.geojson";
};

export const isIFCFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === IFCFileFormat;
};

export const isSHPFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === SHPFileFormat;
};

export const isDXFFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === DXFFileFormat;
};

export const isConfigFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === configFileType;
};

export const isProjectionFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return projFileFormat.indexOf(extension) !== -1;
};

export const isPlanarFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imagePlanarFormat.indexOf(extension) !== -1;
};

export const isPointCloudFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return pointCloudFileFormats.indexOf(extension) !== -1;
};

export const isOrthomosaicFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return imageOrthoFormat.indexOf(extension) !== -1;
};

export const isXmlFile = (filename) => {
  const extension = path.extname(filename).slice(1);
  return extension === xmlFileFormat;
};

const getDXFLayerNames = (text): Promise<{name: string, color: string}[]> => {
  return new Promise((resolve, reject) => {
    const workerURL = "workers/dxf-reader.js";
    let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

    worker.onmessage = event => {
      const {success, response} = event.data;

      if (success) {
        resolve(response);
      } else {
        console.error(response);
        reject(response);
      }
    };

    const data = {text, type: "layers"};
    worker.postMessage(data);
  });
};

const getSHPLayerNames = (buffer) : Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const workerURL = "workers/shp-reader.js";
    let worker = new Worker(`${process.env.PUBLIC_URL}/${workerURL}`);

    worker.onmessage = event => {
      const {success, response} = event.data;

      if (success) {
        resolve(response);
      } else {
        console.error(response);
        reject(response);
      }
    };

    const data = {dbf: buffer, type: "layers"};
    worker.postMessage(data, [data.dbf]);
  });
};

const addIFCFiles = async (dispatch: Dispatch, assetPaths: string[], folderID: string) => {
  let assetsList = [] as AssetInfo[];

  assetPaths.forEach(assetPath => {
    assetsList.push({
      folderID,
      name: path.basename(assetPath),
      path: assetPath,
      type: AssetType.IFC
    });
  });

  dispatch(addAssets(assetsList));
  return assetPaths.length;
};

const addDXFFiles = async (dispatch: Dispatch, assetPaths: string[], folderID: string) => {
  let assetsList = [] as AssetInfo[];

  showBackdrop();

  for (let assetPath of assetPaths) {
    const fileName = path.basename(assetPath);

    try {
      const text = await readFileText(assetPath);
      const layers = await getDXFLayerNames(text);

      const layerData = layers.map(layer => {
        return {
          id: nanoid(),
          name: layer.name,
          visible: true,
          color: layer.color
        };
      }) as LineworkLayerData[];

      assetsList.push({
        folderID,
        name: fileName,
        path: assetPath,
        data: layerData,
        type: AssetType.DXF
      });
    } catch(err) {

      toast.error(t("toast.import-dxf-error", {
        path: fileName
      }));
    }
  }

  hideBackdrop();

  dispatch(addAssets(assetsList));
  return assetsList.length;
};

const addSHPFiles = async (dispatch: Dispatch, assetPaths: string[], folderID: string) => {
  let assetsList = [] as AssetInfo[];

  showBackdrop();

  for (let assetPath of assetPaths) {
    const fileName = path.basename(assetPath);

    try {
      const dbfPath = pathWithExtension(assetPath, "dbf");
      const buffer = await readFileBuffer(dbfPath);
      const layers = await getSHPLayerNames(buffer);

      const layerData = layers.map(layer => {
        return {
          id: nanoid(),
          name: layer,
          visible: true,
          color: DEFAULT_LAYER_COLOR
        };
      }) as LineworkLayerData[];

      assetsList.push({
        folderID,
        name: fileName,
        path: assetPath,
        data: layerData,
        type: AssetType.SHP
      });
    } catch(err) {

      toast.error(t("toast.import-shp-error", {
        path: fileName
      }));
    }
  }

  hideBackdrop();

  dispatch(addAssets(assetsList));
  return assetsList.length;
};

const addOrthoBatch = async (dispatch: Dispatch, assetPaths: string[], folderID: string, orthoQuality: number): Promise<number> => {
  if (assetPaths.length === 0) {
    return 0;
  }

  return new Promise(async resolve => {
    const exe = new PythonExecutable();

    let response;
    let commands = [
      "-p", "convert_geotiff",
      "--image_paths", assetPaths,
      "--output_scale", orthoQuality
    ];

    let numAdded = 0;

    exe.run({
      commands,
      onLine: jsonData => {
        if (jsonData) {
          response = jsonData;
        }
      },
      onClose: () => {
        if (!response) {
          response = [];
        }

        let assetsList = [] as AssetInfo[];

        response.forEach(geotiff => {
          const assetPath = slash(geotiff.file_path);
          const fileName = path.basename(assetPath);

          if (geotiff.error) {
            toast.error(t(`toast.${geotiff.error}`, {
              path: fileName
            }));
            return;
          }

          const {corners, base64, width, height, base64_split,
            scale_x, scale_y, width_original, height_original} = geotiff;

          const orthoData = {
            imageBounds: corners,
            imageDataSplit: base64_split,
            imageData: base64,
            imageSize: [width, height],
            imageSizeOriginal: [width_original, height_original],
            imagePath: assetPath,
            scaleX: scale_x,
            scaleY: scale_y,
          } as OrthoData;

          assetsList.push({
            folderID,
            name: fileName,
            path: assetPath,
            type: AssetType.OrthoMosaic,
            data: orthoData
          });

          numAdded += 1;
        });

        dispatch(addAssets(assetsList));
        resolve(numAdded);
      }
    });
  });
};

const addPlanarFile = (dispatch: Dispatch, assetPath: string, folderID: string):
  Promise<{numAdded: number, numMissing: number}> => {
  return new Promise((resolve, reject) => {
    const exe = new PythonExecutable();

    let cameras;
    let commands = [
      "-p", "convert_engine_planars",
      "--input_path", assetPath
    ];

    exe.run({
      commands,
      onLine: jsonData => {
        if (jsonData) {
          cameras = jsonData;
        }
      },
      onClose: () => {
        if (!cameras) {
          reject();
          return;
        }

        const numCameras = cameras.length;
        const numMissing = cameras.filter(camera => !camera.path).length;
        const numAdded = numCameras - numMissing;

        if (numCameras === 0) {
          reject();
          return;
        }

        let cameraData: CameraData[] = [];

        for (let camera of cameras) {
          let name = camera.path
            ? path.basename(camera.path)
            : camera.name;

          const rowData = {
            id: nanoid(),
            path: camera.path,
            name: name,
            x: camera.x,
            y: camera.y,
            z: camera.z,
            roll: camera.roll,
            pitch: camera.pitch,
            yaw: camera.yaw,
            config: camera.config
          } as CameraData;

          cameraData.push(rowData);
        }

        dispatch(addAsset({
          folderID,
          name: path.basename(assetPath),
          path: assetPath,
          type: AssetType.Planar,
          data: cameraData
        }));

        resolve({numAdded, numMissing});
      }
    });
  });
};

const addPanoramicFile = (dispatch: Dispatch, assetPath: string, folderID: string):
  Promise<{numAdded: number, numMissing: number}> => {
  return new Promise((resolve, reject) => {
    const exe = new PythonExecutable();

    let cameras;
    const inputPath = assetPath;
    const projection = LocalScene.engineStyleProjection(LocalScene.dataProjection);

    let commands = [
      "-p", "convert_engine_panoramics",
      "--input_path", inputPath,
      "--projection", projection
    ];

    exe.run({
      commands,
      onLine: jsonData => {
        cameras = jsonData;
      },
      onClose: async () => {
        if (!cameras) {
          reject({pathError: false});
          return;
        }

        const numCameras = cameras.length;
        const numMissing = cameras.filter(camera => !camera.path).length;
        const numAdded = numCameras - numMissing;

        if (numCameras === 0) {
          reject({pathError: false});
          return;
        }

        let cameraData = [] as CameraData[];

        for (let camera of cameras) {
          let name = camera.path
            ? path.basename(camera.path)
            : camera.name;

          cameraData.push({
            id: nanoid(),
            path: camera.path,
            name: name,
            x: camera.x,
            y: camera.y,
            z: camera.z,
            roll: camera.roll,
            pitch: camera.pitch,
            yaw: camera.yaw
          });
        }

        dispatch(addAsset({
          folderID,
          name: path.basename(assetPath),
          path: assetPath,
          type: AssetType.Panoramic,
          data: cameraData
        }));

        resolve({numAdded, numMissing});
      }
    });
  });
};

const addXMLFile = async (dispatch: Dispatch, assetPath: string, folderID: string) => {
  const parser = new Parser({
    explicitChildren:true,
    preserveChildrenOrder: true
  });

  let text = await fse.readFile(assetPath, {encoding: 'utf8'});
  let textAll = String(text);
  let encoding = textAll.split('?>')[0].split('encoding="')[1].split('"')[0];

  if (encoding !== 'utf-8') {
    if (encoding === 'iso-8859-1') {
      text = await fse.readFile(assetPath, {encoding: 'latin1'});
    } else {
      toast.error(t("dialog.xml-file-encoding-error"));
      return false;
    }
  }

  try {
    let xmlJSON = await parser.parseStringPromise(text);
    if (!("LandXML" in xmlJSON)) {
      toast.error(t("dialog.xml-file-load-error"));
      return false;
    }

    xmlJSON = xmlJSON.LandXML;

    let xmlData = {
      info: xmlJSON.$,
      units: xmlJSON.Units[0].$$[0].$,
      alignments: [],
      enu: false
    } as XMLData;

    xmlJSON.Alignments[0].Alignment.forEach(alignment => {
      let alignObject = {
        id: nanoid(),
        name: alignment.$.name,
        length: parseFloat(alignment.$.length),
        staStart: parseFloat(alignment.$.staStart),
        about: alignment.$.desc,
        geometry: []
      };

      let geometry = alignment.CoordGeom[0].$$;

      if (Array.isArray(geometry)) {
        geometry.forEach(geom => {
          let geometryElement = {shapeType: geom['#name']};
          let dollarSignKeys = Object.keys(geom.$);
          let featureKeys = Object.keys(geom).filter(
            key=> ((key !== '$') && (key !== '$$') && (key !== '#name')));
          dollarSignKeys.forEach(key$ => {
            let aboutValue = geom.$[key$];
            aboutValue = !isNaN(parseFloat(aboutValue))
              ? parseFloat(aboutValue)
              : aboutValue;

            geometryElement[key$] = aboutValue;
          });

          featureKeys.forEach(fKey => {
            let coordValue = geom[fKey];
            let splitCoordValue;
            let tempValue;
            if (Object.keys(coordValue[0]).includes('$')) {
              tempValue = String(coordValue[0]['_']);
            } else {
              tempValue = String(coordValue[0]);
            }

            let newLineSep = tempValue.split('\n');
            let recombine = newLineSep.join(' ');
            splitCoordValue = recombine.split(' ');
            let coordValueCorrected = [];
            splitCoordValue.forEach(scv=> {
              if (!isNaN(parseFloat(scv))) {
                coordValueCorrected.push(parseFloat(scv));
              }
            });
            if (coordValueCorrected.length > 0) {
              geometryElement[fKey] = coordValueCorrected;
            }
          });
          alignObject.geometry.push(geometryElement);
        });
      }

      xmlData.alignments.push(alignObject);
    });

    // Checks to see if length and staStart attributes were correctly added in
    // xml file, and if not, manually calculates them.

    let overwriteLength = false;
    let overwriteStaStart = false;

    xmlData.alignments.forEach(alignment => {
      let staStart = alignment.staStart;
      alignment.geometry.forEach(geom => {
        let keys = Object.keys(geom);
        if (!keys.includes("staStart") || overwriteStaStart) {
          geom['staStart'] = staStart;
          overwriteStaStart = true;
          let length = 0;
          if (!keys.includes("length") || overwriteLength) {
            overwriteLength = true;
            if (geom['shapeType'] === "Line") {
              length = lineDist(geom);
            } else if (geom['shapeType'] === "Curve") {
              length = curveDist(geom);
            }
          }
          if (overwriteLength) {
            geom['length'] = length;
          }
          if (overwriteStaStart) {
            staStart += length;
          }
        }
        geom['checked'] = 1;
      });
    });

    dispatch(addAsset({
      folderID,
      name: path.basename(assetPath),
      path: assetPath,
      type: AssetType.LandXML,
      data: xmlData
    }));

    return true;
  } catch {
    toast.error(t("dialog.xml-file-load-error"));
    return false;
  }
};

const getHeaderIndex = (headers: string[], values: string | string[]) => {
  return typeof(values) === 'string'
    ? headers.indexOf(values)
    : Math.max(...values.map(value => headers.indexOf(value)));
};

const addTagCSV = async (dispatch, assetPath: string, folderID: string): Promise<number> => {
  const text = await readFileText(assetPath);
  const data = CSV.parse(text);

  const headers = data[0].map(x => x.toLowerCase());
  const lines = data.slice(1);

  const xIndex = getHeaderIndex(headers, "easting");
  const yIndex = getHeaderIndex(headers, "northing");
  const zIndex = getHeaderIndex(headers, "elevation");
  const nameIndex = getHeaderIndex(headers, "name");
  const annotationIndex = getHeaderIndex(headers, "annotations");
  const commentIndex = getHeaderIndex(headers, ["comment", "comments", "comment1_value"]);

  const validHeader = (xIndex !== -1) && (yIndex !== -1);
  if (!validHeader) {
    throw new Error("Invalid headers");
  }

  const hasHeight = zIndex !== -1;

  const tags = lines.map((line, index) => {
    let easting = parseFloat(line[xIndex]);
    let northing = parseFloat(line[yIndex]);

    let name = (nameIndex !== -1)
      ? line[nameIndex]
      : `Item ${index}`;

    let comment = (commentIndex !== -1)
      ? line[commentIndex]
      : "";

    let annotations = [];
    if (annotationIndex !== -1) {
      try {
        let text = line[annotationIndex];
        let data = JSON.parse(text);

        annotations = data.map(definition => {
          let id;

          if (definition.properties.id) {
            // Unique id already exists
            id = definition.properties.id;
          } else {
            // Generate a new unique id
            id = nanoid();
            definition.properties.id = id;
          }

          return {
            id,
            definition
          } as TagAnnotation;
        });
      } catch {
        // Nothing
      }
    }

    const tag = {
      id: nanoid(),
      name,
      comment,
      x: easting,
      y: northing,
      date: new Date().toISOString(),
      sceneState: null,
      annotations
    } as TagItemData;

    if (hasHeight) {
      const elevation = parseFloat(line[zIndex]);
      if (elevation && (elevation !== -9999)) {
        tag.z = elevation;
      }
    }

    return tag;
  });

  // Create new tag collection
  const asset = await dispatch(createAsset({
    folderID,
    name: path.basename(assetPath),
    path: assetPath,
    type: AssetType.Tag,
    size: TagSize.Medium,
    texture: defaultTagTexture
  }));

  // Add new tags to collection
  await dispatch(createTags({
    assetID: asset.id,
    tags
  }));

  return tags.length;
};

export const openDialogMulti = (filter): Promise<string[]> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile', 'multiSelections']
  }).then(result => {
    if (result.canceled) {
      return [];
    }

    const filePaths = result.filePaths.map(path => slash(path));
    return filePaths;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return [];
  });
};

export const openDialogFile = (filter): Promise<string> => {
  return dialog.showOpenDialog( {
    filters: filter,
    properties: ['openFile']
  }).then(result => {
    if (result.canceled) {
      return null;
    }

    const filePath = slash(result.filePaths[0]);
    return filePath;
  }).catch(() => {
    dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
    return null;
  });
};

export const openDialogFolder = (): Promise<{directoryPath: string, filePaths: string[]}> => {
  return new Promise((resolve) => {

    dialog.showOpenDialog({
      properties: ['openDirectory']
    }).then(async result => {
      if (result.canceled) {
        resolve({
          directoryPath: '',
          filePaths: []
        });

        return;
      }

      const filePath = slash(result.filePaths[0]);
      const filePaths = await parseDirectory(filePath);

      resolve({
        directoryPath: filePath,
        filePaths: filePaths
      });
    }).catch(() => {
      dialog.showErrorBox(t("dialog.general-error-title"), t("dialog.file-load-error"));
      return null;
    });
  });
};

export const openDirectory = (): Promise<any> => {
  return dialog.showOpenDialog({
    properties: ["openDirectory"]
  }).then(result => {
    if (result.canceled) {
      return null;
    }
    if (result.filePaths) {
      const outputFolder = slash(result.filePaths[0]);
      return outputFolder;
    }

    return null;
  });
};

const parseDirectory = async (directoryPath: string): Promise<string[]> => {
  const entries = await fse.readdir(directoryPath, {withFileTypes: true});

  const files = entries
    .filter(entry => entry.isFile())
    .map(file => path.join(directoryPath, file.name));

  const directories = entries
    .filter(entry => entry.isDirectory())
    .map(directory => path.join(directoryPath, directory.name));

  for (let folder of directories) {
    files.push(...(await parseDirectory(folder)));
  }

  return files;
};
